/**
 * Canvas Assumption & Configuration Bridge
 *
 * Decouples the primary application store (and startup bundle) from heavy
 * simulation solvers, contracts, and parameter migrations.
 *
 * When the simulation editor loads, it registers the complete simulation
 * implementation. Safe, lightweight fallbacks are provided when not loaded.
 */

import type { Connection, Edge, Node as AppNode } from 'reactflow';
import type { EoStudySpec } from '../lib/sim/eo/study/eoStudySpec';
import type { EoTransientConfig } from '../lib/sim/eo/eoTransientConfig';
import type { EoGraphConfigV1 } from '../lib/sim/eo/eoGraphConfig';
import type {
  EoCanvasExecutionConfigV1,
  NormalizedEoCanvasExecutionConfig,
} from '../lib/sim/eo/eoCanvasExecutionConfig';
import type { EoCanvasDerivedStreamComposition } from '../lib/sim/eo/connectionValidation';
import type {
  InUseAssumptionTabFields,
  InUseModelAssumptionTopology,
} from '../lib/sim/canvas/inUseAssumptions';

export type { InUseAssumptionTabFields, InUseModelAssumptionTopology };

export interface CanvasAssumptionHandlers {
  acceptInUseCanvasAssumptions: <
    N extends AppNode = AppNode,
    T extends InUseAssumptionTabFields<N> = InUseAssumptionTabFields<N>,
  >(
    fields: T,
    topology?: InUseModelAssumptionTopology,
  ) => T;
  normalizeEoStudyDefinition: (input: unknown) => { spec: EoStudySpec };
  defaultEoStudySpec: () => EoStudySpec;
  normalizeEoTransientConfig: (input: unknown) => { config: EoTransientConfig };
  defaultEoTransientConfig: () => EoTransientConfig;
  parseEoCanvasExecutionConfigOrDefault: (input: unknown) => EoCanvasExecutionConfigV1;
  defaultEoCanvasExecutionConfig: () => EoCanvasExecutionConfigV1;
  cloneEoGraphConfigForRepair: (config?: EoGraphConfigV1) => EoGraphConfigV1 | undefined;
  cloneEoTransientConfigForRepair: (config?: EoTransientConfig) => EoTransientConfig | undefined;
  normalizeEoCanvasExecutionConfig: (config: unknown) => NormalizedEoCanvasExecutionConfig;
  parseEoCanvasExecutionConfig: (config: unknown) => EoCanvasExecutionConfigV1;
  deriveEoGraphV2ConnectionComposition: (
    connection: Connection,
    nodes: readonly AppNode[],
    edges: readonly Edge[],
  ) => EoCanvasDerivedStreamComposition | null;
  retireInvalidCanvasResultEdges: (edges: readonly Edge[]) => Edge[];
}

let activeHandlers: CanvasAssumptionHandlers | undefined;
let loadingHandlers: Promise<void> | undefined;

/** Load the canonical implementation before opening a canvas from a site page. */
export function ensureCanvasAssumptionBridge(): Promise<void> {
  if (activeHandlers) return Promise.resolve();
  if (!loadingHandlers) {
    loadingHandlers = import('./canvasAssumptionBridgeImplementation')
      .then(module => module.initCanvasAssumptionBridge())
      .catch(error => { loadingHandlers = undefined; throw error; });
  }
  return loadingHandlers;
}

function handlers(): CanvasAssumptionHandlers {
  if (!activeHandlers) {
    throw new Error('Load the simulation workspace before changing canvas assumptions.');
  }
  return activeHandlers;
}

export function registerCanvasAssumptionHandlers(handlers: CanvasAssumptionHandlers): void {
  activeHandlers = handlers;
}

export function acceptInUseCanvasAssumptions<
  N extends AppNode = AppNode,
  T extends InUseAssumptionTabFields<N> = InUseAssumptionTabFields<N>,
>(fields: T, topology?: InUseModelAssumptionTopology): T {
  return handlers().acceptInUseCanvasAssumptions(fields, topology);
}

export function normalizeEoStudyDefinition(input: unknown) {
  return handlers().normalizeEoStudyDefinition(input);
}

export function defaultEoStudySpec() {
  return handlers().defaultEoStudySpec();
}

export function normalizeEoTransientConfig(input: unknown) {
  return handlers().normalizeEoTransientConfig(input);
}

export function defaultEoTransientConfig() {
  return handlers().defaultEoTransientConfig();
}

export function parseEoCanvasExecutionConfigOrDefault(input: unknown) {
  return handlers().parseEoCanvasExecutionConfigOrDefault(input);
}

export function defaultEoCanvasExecutionConfig() {
  return handlers().defaultEoCanvasExecutionConfig();
}

export function cloneEoGraphConfigForRepair(config?: EoGraphConfigV1) {
  return handlers().cloneEoGraphConfigForRepair(config);
}

export function cloneEoTransientConfigForRepair(config?: EoTransientConfig) {
  return handlers().cloneEoTransientConfigForRepair(config);
}

export function normalizeEoCanvasExecutionConfig(config: unknown) {
  return handlers().normalizeEoCanvasExecutionConfig(config);
}

export function parseEoCanvasExecutionConfig(config: unknown) {
  return handlers().parseEoCanvasExecutionConfig(config);
}

export function deriveEoGraphV2ConnectionComposition(
  connection: Connection,
  nodes: readonly AppNode[],
  edges: readonly Edge[],
) {
  return handlers().deriveEoGraphV2ConnectionComposition(connection, nodes, edges);
}

export function retireInvalidCanvasResultEdges(edges: readonly Edge[]) {
  return handlers().retireInvalidCanvasResultEdges(edges);
}

